<template>
    <v-card flat>
        <v-card-title>
            <div class="secondary--text">Credit quota schedule</div>
        </v-card-title>
        <v-card-text>
            <v-data-table
                item-key="sclid"
                :headers="headers"
                :items="filteredHistory"
                :loading="fetchingHistory"
                loading-text="Loading credit quota schedule..."
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                :item-class="setRowClass">
                <template v-slot:top="{ pagination, options, updateOptions }">
                    <div class="d-flex align-center">
                        <instance-credit-quota-edit-dialog crud="create" :min-create-end-date="lastEndDate" @success="refreshQuotaData()">
                            <v-btn color="primary" class="mr-3">
                                <v-icon left>add</v-icon>
                                Quota
                            </v-btn>
                        </instance-credit-quota-edit-dialog>
                        <v-sheet rounded class="d-flex align-center py-2 px-4">
                            <v-switch v-model="showOldSchedules" inset dense hide-details label="Show past schedules too" class="my-0 pa-0"></v-switch>
                        </v-sheet>
                        <v-spacer></v-spacer>
                        <v-data-footer
                            :pagination="pagination"
                            :options="options"
                            @update:options="updateOptions"
                            :itemsPerPageOptions="itemsPerPageOptions"
                            items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                            class="no-border" />
                    </div>
                </template>
                <template v-slot:no-data>
                    <v-alert type="error" text class="ma-4" v-if="historyError">{{ historyErrorMsg }}</v-alert>
                    <span v-else>No credit quota schedule found.</span>
                </template>
                <template v-slot:[`item.limit`]="{ item }">
                    <span>{{ toLocaleFixed(item.limit, $appConfig?.VUE_APP_DASHBOARD_PRECISION) }}</span>
                </template>
                <template v-slot:[`item.state`]="{ item }">
                    <v-chip small :color="stateColor(item)">{{ stateLabel(item) }}</v-chip>
                </template>
                <template v-slot:[`item.reset_counter`]="{ item }">
                    <span>{{ item.carry_over ? 'No' : 'Yes' }}</span>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-menu left offset-x nudge-top="10">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" text color="secondary" :disabled="Boolean(activeActionKey)" small>
                                <v-icon>more_horiz</v-icon>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <instance-credit-quota-edit-dialog v-if="!item.past" :quota="item" @success="refreshQuotaData()">
                                <v-list-item :disabled="Boolean(activeActionKey)">
                                    <v-list-item-icon>
                                        <v-icon color="secondary">mdi-pencil-outline</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title class="secondary--text">Edit</v-list-item-title>
                                </v-list-item>
                            </instance-credit-quota-edit-dialog>
                            <v-divider v-if="!item.past" class="my-2"></v-divider>
                            <v-list-item @click="deleteSchedule(item)" :disabled="Boolean(activeActionKey)">
                                <v-list-item-icon>
                                    <v-progress-circular v-if="activeActionKey === `delete-${item.sclid}`" size="18" width="2" indeterminate color="error" />
                                    <v-icon v-else color="error">mdi-trash-can-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title class="error--text">Delete</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </template>
            </v-data-table>
        </v-card-text>
    </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { toLocaleFixed } from '@/utils'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as applicationErrors from '@/modules/application/errors'
import * as instanceErrors from '@/modules/instance/errors'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as resourcePoolErrors from '@/modules/user/errors/resourcePools'
import * as spaceErrors from '@/modules/space/errors'
import * as tablesErrors from '@/modules/snapshot/errors/tables'

const { getBackendErrorMessage } = createBackendErrorHandler(
    applicationErrors,
    instanceErrors,
    lifecycleErrors,
    permissionsErrors,
    resourcePoolErrors,
    spaceErrors,
    tablesErrors
)

export default {
    name: 'InstanceCreditQuotaScheduleTable',
    components: {
        InstanceCreditQuotaEditDialog: () => import('@/modules/space/components/InstanceCreditQuotaEditDialog.vue')
    },
    data() {
        return {
            activeActionKey: '',
            showOldSchedules: false,
            headers: [
                { text: 'State', value: 'state', align: 'start', sortable: false },
                { text: 'End date', value: 'end_date', align: 'start' },
                { text: 'Quota', value: 'limit', align: 'start' },
                { text: 'Reset counter', value: 'reset_counter', align: 'start' },
                { text: 'Actions', align: 'right', value: 'actions', sortable: false }
            ],
            itemsPerPageOptions: [5, 20, 100, -1]
        }
    },
    computed: {
        ...mapState('spaceStore', {
            fetchingHistory: state => state.studentQuotaFetchingHistory,
            historyError: state => state.studentQuotaHistoryError,
            historyErrorMsg: state => state.studentQuotaHistoryErrorMsg,
            history: state => state.studentQuotaHistory,
            currentSclid: state => state.studentQuotaCurrent?.sclid ?? null,
            lastEndDate: state => state.studentQuotaLastEndDate
        }),
        filteredHistory() {
            if (this.showOldSchedules) {
                return this.history
            }

            return this.history.filter(schedule => !schedule.past)
        }
    },
    created() {
        if (!this.history.length && !this.fetchingHistory) {
            this.refreshQuotaData()
        }
    },
    methods: {
        toLocaleFixed,
        ...mapActions('spaceStore', ['refreshStudentQuotaData']),
        refreshQuotaData() {
            this.refreshStudentQuotaData(this.$route.params.sid)
        },
        stateLabel(item) {
            if (item.sclid === this.currentSclid) {
                return 'Current'
            }
            if (item.past) {
                return 'Past'
            }
            return 'Future'
        },
        stateColor(item) {
            if (item.sclid === this.currentSclid) {
                return 'primary'
            }
            if (item.past) {
                return 'grey lighten-4'
            }
            return 'blue lighten-5'
        },
        setRowClass(item) {
            if (item.past) {
                return 'text--disabled'
            }
        },
        async deleteSchedule(item) {
            this.activeActionKey = `delete-${item.sclid}`
            try {
                await this.$axios.delete(`/space_credit_limit/${item.sclid}/cancel`, {
                    data: { end_date: item.end_date }
                })
                await this.refreshStudentQuotaData(this.$route.params.sid)
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, error.response?.data?.debug || 'Could not delete schedule entry')
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
            } finally {
                this.activeActionKey = ''
            }
        }
    }
}
</script>
